import { Badge, Card, Table } from "antd";
import camelCase from "lodash.camelcase";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import truncate from "lodash.truncate";

import { userSelectors } from "@fitness-app/app-store";
import {
  ProductChargeType,
  ProductStatus,
  type Product,
  type ProductStatusEnum,
} from "@fitness-app/data-models/entities/Product";

import { supabase, useAppSelector } from "~/store/initializeStore";

type CatalogProduct = Pick<Product, "id" | "name" | "description" | "images" | "type" | "chargeType" | "status">;

// Widok dla dostępu materiałowego (products.read / products.resources.*):
// te same kolumny opisowe co lista ownera, bez części commerce — kont
// płatności, liczby klientek, cen i przełącznika statusu.
const ProductResourcesCatalog = (): React.ReactElement => {
  const { t } = useTranslation(["products", "common"]);
  const userId = useAppSelector((state) => state.user.data?.id);
  const { trainerId } = useAppSelector(userSelectors.getUserClaims);
  const ownerId = trainerId ?? userId;
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["product-resources-catalog", ownerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product")
        .select("id, name, description, images, type, chargeType, status")
        .eq("owner", ownerId ?? "")
        .order("createdAt", { ascending: false })
        .returns<CatalogProduct[]>();

      if (error) {
        throw error;
      }

      return data;
    },
    enabled: Boolean(ownerId),
  });

  return (
    <Card title={t("productResources.list")}>
      <Table<CatalogProduct>
        loading={isLoading}
        dataSource={products}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{ hideOnSinglePage: true, pageSize: 100 }}
      >
        <Table.Column<CatalogProduct>
          title={t("list.columns.name")}
          dataIndex="name"
          key="name"
          render={(name: string, row) => (
            <span className="flex items-center gap-2">
              {row.images?.length ? <img alt="product" src={row.images[0]} className="h-auto max-w-[50px]" /> : null}
              {name}
            </span>
          )}
        />
        <Table.Column<CatalogProduct>
          title={t("list.columns.description")}
          dataIndex="description"
          key="description"
          render={(description: string | null) => truncate(description ?? "", { length: 80 })}
        />
        <Table.Column<CatalogProduct>
          title={t("list.columns.type")}
          dataIndex="type"
          key="type"
          render={(type: string) => t(`productType.${camelCase(type)}`)}
        />
        <Table.Column<CatalogProduct>
          title={t("list.columns.paymentType")}
          dataIndex="chargeType"
          key="chargeType"
          render={(_, row) => t(`chargeType.${row.chargeType === ProductChargeType.FREE ? "free" : "paid"}`)}
        />
        <Table.Column<CatalogProduct>
          title={t("list.columns.active")}
          dataIndex="status"
          key="status"
          render={(status: ProductStatusEnum) =>
            status === ProductStatus.ACTIVE ? (
              <Badge status="success" text={t("common:yes")} />
            ) : (
              <Badge status="default" text={t("common:no")} />
            )
          }
        />
        <Table.Column<CatalogProduct>
          title={t("list.columns.options")}
          key="options"
          render={(_, row) => (
            <Link key="resources" to={`/products/${row.id}/resources`}>
              {t("productResources.title")}
            </Link>
          )}
        />
      </Table>
    </Card>
  );
};

export default ProductResourcesCatalog;
